import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const BUCKET = "listing-photos";
const SIGN_TTL = 60 * 60; // 1 hour

// In-memory cache: path -> { url, expiresAt }
const cache = new Map<string, { url: string; expiresAt: number }>();

/**
 * Accepts either a storage path (e.g. "uid/uuid.jpg") or a legacy
 * public URL pointing at /storage/v1/object/public/listing-photos/...
 * and returns the bucket-relative path.
 */
export function extractPhotoPath(value: string | null | undefined): string | null {
  if (!value) return null;
  if (!/^https?:\/\//i.test(value)) return value.replace(/^\/+/, "");
  const marker = `/${BUCKET}/`;
  const idx = value.indexOf(marker);
  if (idx === -1) return null;
  return value.substring(idx + marker.length).split("?")[0];
}

export async function getSignedListingPhotoUrl(value: string): Promise<string | null> {
  const path = extractPhotoPath(value);
  if (!path) return null;
  const cached = cache.get(path);
  if (cached && cached.expiresAt > Date.now() + 30_000) return cached.url;

  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, SIGN_TTL);
  if (error || !data?.signedUrl) return null;
  cache.set(path, { url: data.signedUrl, expiresAt: Date.now() + SIGN_TTL * 1000 });
  return data.signedUrl;
}

export function useSignedListingPhoto(value: string | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!value) {
      setUrl(null);
      return;
    }
    getSignedListingPhotoUrl(value).then((u) => {
      if (!cancelled) setUrl(u);
    });
    return () => {
      cancelled = true;
    };
  }, [value]);
  return url;
}
